import { ISelectItem } from "@components/inputs/select/Select.types";
import { IFieldDef, IGetValueArgs, isRequiredByPath, TInfoValue } from "@components/smart/FieldInfo";
import {
    EmailDef,
    getAddressFields,
    hasOptionalPostalCode,
    joinAddressToStreetString,
    PhoneNumberDef,
    phoneNumberInputFormatter,
    SingleBusinessPartnerDef,
    splitAddressNumber
} from "@components/smart/GeneralFieldDefinition";
import { getCollapsedGroupId } from "@components/smart/Smart.utils";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { IDependentFieldDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { invalidateItems } from "@components/smart/smartSelect/SmartSelectAPI";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { isODataError } from "@odata/Data.types";
import {
    AccountAssignmentSelectionEntity,
    BankTransactionEntity,
    BusinessPartnerBankAccountEntity,
    BusinessPartnerContactEntity,
    BusinessPartnerEntity,
    DocumentBusinessPartnerEntity,
    DocumentEntity,
    EntitySetName,
    IBankAccountEntity,
    IBankTransactionBusinessPartnerEntity,
    IBusinessPartnerBankAccountEntity,
    IBusinessPartnerContactEntity,
    IBusinessPartnerEntity,
    IBusinessPartnerIssuedDocumentDefaultEntity,
    IBusinessPartnerReceivedDocumentDefaultEntity,
    ICompanyBankAccountEntity,
    ICountryEntity,
    IDocumentBusinessPartnerEntity,
    IDocumentItemEntity,
    IPrDeductionBusinessPartnerEntity
} from "@odata/GeneratedEntityTypes";
import {
    BankAccountRegistrationStatusCode,
    CountryCode,
    CurrencyCode,
    DocumentTypeCode,
    PayerReliabilityStatusCode,
    PaymentMethodCode,
    SelectionCode,
    VatStatusCode
} from "@odata/GeneratedEnums";
import { BatchRequest, OData } from "@odata/OData";
import { ODataError, parseResponse } from "@odata/ODataParser";
import { invalidateItemTemplateSelectItems } from "@pages/admin/itemTemplates/ItemTemplates.utils.shared";
import { getAgendaCountryCode, getCompanyUsedCurrencyCodes } from "@pages/companies/Company.shared.utils";
import { createAccountFromIBAN, createIBANFromAccount } from "@utils/BankUtils";
import {
    getCompanyCurrency,
    hasEverBeenVatRegisteredCompany,
    isAccountAssignmentCompany,
    isIdentifiedPersonCompany
} from "@utils/CompanyUtils";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import { areObjectsEqual, getValue, isDefined, isNotDefined, isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import i18next from "i18next";
import IBAN from "iban";
import { cloneDeep } from "lodash";
import * as yup from 'yup';

import { ARES_GET_API, ARES_GET_PAYER_STATUS_API, REST_API_URL } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    CacheStrategy,
    FieldType,
    GroupedField,
    HTTPStatusCode,
    NavigationSource,
    Status,
    TextAlign
} from "../../enums";
import { TRecordAny } from "../../global.types";
import { setGroupStatus } from "../../views/formView/Form.utils";
import { FormStorage, IFormStorageDefaultCustomData } from "../../views/formView/FormStorage";
import {
    convertAccountAssignmentIntoSelection,
    loadAccountAssignments,
    rewriteAccountAssignment
} from "../accountAssignment/AccountAssignment.utils";
import {
    appendAdditionalItemsToSavedBankAccounts,
    BankAccountDependentFields,
    IBankAccountArgs,
    IBankAccountsCustomData,
    prepareSavedAccounts,
    TBankAccountRelatedEntity,
    updateAllSavedAccountsOnPartnerChange,
    validateAccountNumber
} from "../banks/bankAccounts/BankAccounts.utils";
import { isReceived, isSupportingAccountReliability, refreshExchangeRate } from "../documents/Document.utils";
import { IDocumentCustomData, IDocumentExtendedEntity } from "../documents/DocumentInterfaces";
import { TFieldDefinition, TFieldsDefinition } from "../PageUtils";


export const ReceivedBankAccountDefaultsPath = "##ReceivedBankAccount##";
export const ShowReceivedDefaultsPath = "##ShowReceivedDefaults##";
export const ShowIssuedDefaultsPath = "##ShowIssuedDefaults##";

export interface IBusinessPartnerEntityExtended extends IBusinessPartnerEntity {
    [ReceivedBankAccountDefaultsPath]?: number;
    [ShowReceivedDefaultsPath]?: boolean;
    [ShowIssuedDefaultsPath]?: boolean;
}

export interface IBusinessPartnerCustomData extends IFormStorageDefaultCustomData {
    businessPartnerContacts?: IBusinessPartnerContactEntity[];
    // true if the current business partner was selected from ARES select
    isBusinessPartnerFromAres?: boolean;
    aresBankAccounts?: IBankAccountEntity[];
    countries?: ICountryEntity[];
}


export const BPAccountSchema = yup
  .object({
    AccountNumber: yup.string().trim().nullable(),
    BankCode: yup.string().trim().nullable(),
    Iban: yup.string().trim().nullable(),
    Swift: yup.string().trim().nullable(),
    CountryCode: yup.string().trim().nullable(),
  })
  .test(
    "valid-account",
    "Account must have IBAN or account number + bank code",
    (value) => {
      if (!value) return false;

      const isValidIban = IBAN.isValid(value.Iban?.trim());
      const acc = value.AccountNumber?.trim();
      const code = value.BankCode?.trim();
      const countryCode = value.CountryCode ? value.CountryCode.trim() : (isValidIban ? value?.Iban?.substring(0, 2) : "");
      const bankAccountValid = !!countryCode && validateAccountNumber(code as CountryCode, acc) === true;

      return isValidIban || (!!acc && !!code && !!countryCode && bankAccountValid);
    }
  );

export const BankCoreBusinessPartnerAdditionalProperties = [{
    id: createPath(DocumentEntity.BusinessPartner, BusinessPartnerEntity.Name)
}, {
    id: createPath(DocumentEntity.BusinessPartner, BusinessPartnerEntity.LegalNumber)
}, {
    id: createPath(DocumentEntity.BusinessPartner, BusinessPartnerEntity.VatStatus)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.TaxNumber)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.FirstName)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.LastName)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.PhoneNumber)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.Email)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.Street)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.City)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.Country)
}, {
    id: createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.PostalCode)
}];


export const getBusinessPartnerBankAccountAdditionalProperties = (prefix = "", withoutIsDefaultForRecDocs?: boolean): IFieldDef[] => {
    const fields = [
        BusinessPartnerBankAccountEntity.CountryCode, BusinessPartnerBankAccountEntity.AccountNumber,
        BusinessPartnerBankAccountEntity.BankCode, BusinessPartnerBankAccountEntity.IBAN,
        BusinessPartnerBankAccountEntity.SWIFT, BusinessPartnerBankAccountEntity.AbaNumber
    ];

    if (!withoutIsDefaultForRecDocs) {
        fields.push(BusinessPartnerBankAccountEntity.IsDefaultForReceivedDocuments);
    }

    return fields.map(field => ({ id: createPath(prefix, field) }));
};

export const billingAddressStreetFormatter: TFormatterFn = (_, options) => {
    return joinAddressToStreetString(options.item.BillingAddress);
};

export const businessPartnerDependentFields = (bpPath?: string, nameProp?: string): IDependentFieldDef[] => {
    const isNested = bpPath === "";
    const path = isNested ? "" : `${bpPath ?? "BusinessPartner"}/`;
    const res = [{
        from: { id: "LegalNumber" },
        to: { id: `${path}LegalNumber` }
    }, {
        from: { id: "Name" },
        to: { id: `${path}${nameProp ?? "Name"}` }
    }, {
        from: { id: "TaxNumber" },
        to: { id: `${path}TaxNumber` }
    }, {
        from: { id: "VatStatus" },
        to: { id: `${path}VatStatus` }
    }, {
        from: { id: "Contacts/0/FirstName" },
        to: { id: `${path}FirstName` }
    }, {
        from: { id: "Contacts/0/LastName" },
        to: { id: `${path}LastName` }
    }, {
        from: { id: "Contacts/0/PhoneNumber" },
        to: { id: `${path}PhoneNumber` }
    }, {
        from: { id: "Contacts/0/Email" },
        to: { id: `${path}Email` }
    }, {
        from: { id: "BillingAddress/Street" },
        to: { id: `${path}Street` },
        formatter: billingAddressStreetFormatter
    }, {
        from: { id: "BillingAddress/City" },
        to: { id: `${path}City` }
    }, {
        from: { id: "BillingAddress/Country" },
        to: { id: `${path}Country` },
        ignoreNullValue: true
    }, {
        from: { id: "BillingAddress/PostalCode" },
        to: { id: `${path}PostalCode` }
    }];

    if (!isNested) {
        res.push({
            from: { id: "Id" },
            to: { id: `${path}BusinessPartner` }
        }, {
            from: { id: "PayerReliabilityStatusCode" },
            to: { id: `${path}PayerReliabilityStatusCode` }
        });
    }

    return res;
};

export const bpAsyncDepFields: IDependentFieldDef[] = [
    {
        from: { id: "Id" },
        to: { id: `BusinessPartner/Id` }
    }, {
        from: { id: "LegalNumber" },
        to: { id: `LegalNumber` }
    }, {
        from: { id: "Name" },
        to: { id: `Name` }
    }, {
        from: { id: "TaxNumber" },
        to: { id: `TaxNumber` }
    }, {
        from: { id: "VatStatus" },
        to: { id: `VatStatus` }
    }, {
        from: { id: "Contacts/FirstName" },
        localFrom: { id: "Contacts/0/FirstName" },
        to: { id: `FirstName` }
    }, {
        from: { id: "Contacts/LastName" },
        localFrom: { id: "Contacts/0/LastName" },
        to: { id: `LastName` }
    }, {
        from: { id: "Contacts/PhoneNumber" },
        localFrom: { id: "Contacts/0/PhoneNumber" },
        to: { id: `PhoneNumber` }
    }, {
        from: { id: "Contacts/Email" },
        localFrom: { id: "Contacts/0/Email" },
        to: { id: `Email` }
    }, {
        from: { id: "BillingAddress/Street" },
        to: { id: `Street` },
        formatter: billingAddressStreetFormatter
    }, {
        from: { id: "BillingAddress/City" },
        to: { id: `City` }
    }, {
        from: { id: "BillingAddress/Country" },
        to: { id: `Country` },
        ignoreNullValue: true
    }, {
        from: { id: "BillingAddress/PostalCode" },
        to: { id: `PostalCode` }
    }, {
        from: { id: "PayerReliabilityStatusCode" },
        to: { id: `PayerReliabilityStatusCode` }
    }, {
        from: { id: "VatStatusCode" },
        to: { id: `VatStatusCode` }
    }
];

export const bankBpDepFields = bpAsyncDepFields.map(field => ({
    ...field,
    to: { id: `BusinessPartner/${field.to.id}` },
    from: { id: `BusinessPartner/${field.from.id}` },
    localFrom: field.localFrom ? { id: `BusinessPartner/${field.localFrom?.id}` } : undefined
}));


export const accountPartnerDepFields = businessPartnerDependentFields().map(field => ({
    ...field,
    from: { id: `BusinessPartner/${field.from.id}` }
}));

export const BusinessPartnerDef = (bpPath?: string, nameProp?: string): TFieldDefinition => ({
    ...SingleBusinessPartnerDef,
    fieldSettings: {
        ...SingleBusinessPartnerDef.fieldSettings,
        localDependentFields: businessPartnerDependentFields(bpPath, nameProp),
        loadFromDb: true
    },
    columns: [{ id: BusinessPartnerEntity.Name }, { id: BusinessPartnerEntity.LegalNumber, textAlign: TextAlign.Right }]
});

// todo: refactor this, consider BankTransactions, where is not BP entity created and DocumentBP is optional,
//  invoices, where it is always required and liabilities, where BP is optional but should respect DocumentBP metadata
const isSubFieldRequired = (args: IGetValueArgs, isBPRequired: TInfoValue<boolean>, bpPath = "BusinessPartner") => {
    const { entity } = args.storage.data; // some document or bankTransaction
    const isRequired = getValue<boolean>(isBPRequired, args);
    // if BP is not required, we don't need to check the subfields (e.g. in case of bank transaction)
    // except VatStatus, which is always required when BP is defined
    if (!isRequired && args.bindingContext.getPath() !== BusinessPartnerEntity.VatStatus) {
        return false;
    }
    if (entity[bpPath]?.VatStatus?.Code !== VatStatusCode.NotVATRegistered || args.bindingContext.getPath() === BusinessPartnerEntity.VatStatus) {
        return isRequired || !!(entity[bpPath]?.Name || entity[bpPath]?.LegalNumber);
    }
    return false;
};

export function updateBpValidationResult(storage: FormStorage, bp: IBusinessPartnerEntity, bpBc: BindingContext): void {
    if (isIdentifiedPersonCompany(storage.context)) {
        return null;
    }
    const validationResult = isNotDefined(bp.PayerReliabilityStatusCode) || bp.PayerReliabilityStatusCode === PayerReliabilityStatusCode.Unknown ? null :
        bp.PayerReliabilityStatusCode === PayerReliabilityStatusCode.Unreliable ? {
            status: Status.Warning,
            message: storage.t("Document:Form.BusinessPartnerIsUnreliable")
        } : {
            status: Status.Success,
            message: storage.t("Document:Form.BusinessPartnerIsReliable")
        };
    storage.setAdditionalFieldData(bpBc, "validationResult", validationResult);
}

export function createContactsItems(storage: FormStorage, labelProp: string): ISelectItem[] {
    const contacts: IBusinessPartnerContactEntity[] = storage.data.entity.BusinessPartner?.BusinessPartner?.Contacts;
    return contacts?.map(contact => {
        const id = contact[labelProp as keyof typeof contact] as string;
        const formattedPhone = phoneNumberInputFormatter(contact.PhoneNumber);

        return {
            id,
            label: id,
            additionalData: {
                Email: contact.Email,
                PhoneNumber: contact.PhoneNumber,
                FirstName: contact.FirstName,
                LastName: contact.LastName
            },
            tabularData: [contact.Email, formattedPhone, contact.FirstName, contact.LastName]
        };
    });
}

const bpLocalDependentFields = [{
    from: { id: BusinessPartnerContactEntity.PhoneNumber },
    to: { id: BusinessPartnerContactEntity.PhoneNumber },
    navigateFrom: NavigationSource.Parent
}, {
    from: { id: BusinessPartnerContactEntity.FirstName },
    to: { id: BusinessPartnerContactEntity.FirstName },
    navigateFrom: NavigationSource.Parent
}, {
    from: { id: BusinessPartnerContactEntity.LastName },
    to: { id: BusinessPartnerContactEntity.LastName },
    navigateFrom: NavigationSource.Parent
}, {
    from: { id: BusinessPartnerContactEntity.Email },
    to: { id: BusinessPartnerContactEntity.Email },
    navigateFrom: NavigationSource.Parent
}];

const createBpFieldSettings = (fieldName: string) => {
    return {
        allowCreate: true,
        localDependentFields: bpLocalDependentFields?.filter(field => field.from.id !== fieldName),
        itemsFactory: (args: IGetValueArgs) => {
            return Promise.resolve(createContactsItems(args.storage as FormStorage, fieldName));
        }
    };
};

interface IBPFieldDefArgs {
    transFile?: string;
    isRequired?: TInfoValue<boolean>;
    navigationPath?: string;
    nameProp?: string;
}

export const getBusinessPartnerFieldDef = (propName: string, args: IBPFieldDefArgs): TFieldDefinition => {
    const { isRequired, navigationPath, transFile, nameProp } = args ?? {};

    const bpPath = navigationPath === "" ? navigationPath : `${navigationPath ?? "BusinessPartner"}`;

    const getNestedPath = (field: string) => {
        return `${bpPath ? bpPath + "/" : ""}${field}`;
    };
    const addressFields = getAddressFields(bpPath);
    const creatingKey = transFile && i18next.exists(`${transFile}:FormGroup.NewPartner`) ? `${transFile}:FormGroup.NewPartner` : "Common:General.NewPartner";

    switch (propName) {
        case nameProp ?? BusinessPartnerEntity.Name:
            return {
                groupedField: GroupedField.WrappedMultiStart,
                ...BusinessPartnerDef(bpPath, nameProp),
                width: BasicInputSizes.XL,
                isRequired
            };
        case BusinessPartnerEntity.LegalNumber:
            return {
                ...BusinessPartnerDef(bpPath, nameProp),
                width: BasicInputSizes.S
            };
        case BusinessPartnerEntity.TaxNumber:
            return {
                groupedField: GroupedField.WrappedMultiEnd,
                creatingTitle: i18next.t(creatingKey),
                collapsedRows: [
                    [
                        { id: `${getNestedPath("VatStatus")}` },
                        { id: `${getNestedPath("Street")}` },
                        { id: `${getNestedPath("City")}` },
                        { id: `${getNestedPath("PostalCode")}` },
                        { id: `${getNestedPath("Country")}` },
                    ],
                    [
                        { id: `${getNestedPath("Email")}` },
                        { id: `${getNestedPath("PhoneNumber")}` },
                        { id: `${getNestedPath("FirstName")}` },
                        { id: `${getNestedPath("LastName")}` },
                    ]
                ]
            };

        case BusinessPartnerEntity.VatStatus:
            return {
                type: FieldType.ComboBox,
                isRequired: (args) => isSubFieldRequired(args, isRequired, bpPath),
                cacheStrategy: CacheStrategy.EndOfTime,
                defaultValue: VatStatusCode.NotVATRegistered,
                fieldSettings: {
                    displayName: "Name"
                }
            };
        // documentBusinessPartnerFields
        case DocumentBusinessPartnerEntity.Street:
            return {
                ...addressFields[`${getNestedPath(DocumentBusinessPartnerEntity.Street)}`],
                isRequired: (args) => isSubFieldRequired(args, isRequired, bpPath)
            };
        case DocumentBusinessPartnerEntity.City:
            return {
                ...addressFields[`${getNestedPath(DocumentBusinessPartnerEntity.City)}`],
                isRequired: (args) => isSubFieldRequired(args, isRequired, bpPath)
            };
        case DocumentBusinessPartnerEntity.PostalCode:
            return {
                ...addressFields[`${getNestedPath(DocumentBusinessPartnerEntity.PostalCode)}`],
                isRequired: (args) => isSubFieldRequired(args, isRequired, bpPath) && !hasOptionalPostalCode(args)
            };
        case DocumentBusinessPartnerEntity.Country:
            return {
                ...addressFields[`${getNestedPath(DocumentBusinessPartnerEntity.Country)}`],
                isRequired: (args) => isSubFieldRequired(args, isRequired, bpPath)
            };
        case DocumentBusinessPartnerEntity.Email:
            return {
                ...EmailDef,
                type: FieldType.Autocomplete,
                fieldSettings: createBpFieldSettings(BusinessPartnerContactEntity.Email),
            };
        case DocumentBusinessPartnerEntity.PhoneNumber:
            return {
                ...PhoneNumberDef,
                // todo todo: check out why the autocomplete is not working for phone number, it should be similar to email
                // TODO: For now disable the settings as select changes need to be done
                // fieldSettings: createBpFieldSettings(BusinessPartnerContactEntity.PhoneNumber),
                // type: FieldType.Autocomplete
            };

        case DocumentBusinessPartnerEntity.FirstName:
            return {
                type: FieldType.Autocomplete,
                fieldSettings: createBpFieldSettings(BusinessPartnerContactEntity.FirstName)
            };
        case DocumentBusinessPartnerEntity.LastName:
            return {
                type: FieldType.Autocomplete,
                fieldSettings: createBpFieldSettings(BusinessPartnerContactEntity.LastName)
            };

        default:
            logger.warn(`BusinessPartner.utils.ts: getBusinessPartnerFieldDef: Unknown property name ${propName}`,);
            return {};
    }
};

export const getBusinessPartnerFieldDefs = (args?: IBPFieldDefArgs): TFieldsDefinition => {
    const { navigationPath, nameProp } = args ?? {};
    const bpPath = navigationPath === "" ? navigationPath : `${navigationPath ?? "BusinessPartner"}/`;

    return [
        nameProp ?? BusinessPartnerEntity.Name,
        BusinessPartnerEntity.LegalNumber,
        BusinessPartnerEntity.TaxNumber,
        BusinessPartnerEntity.VatStatus,
        DocumentBusinessPartnerEntity.Street,
        DocumentBusinessPartnerEntity.City,
        DocumentBusinessPartnerEntity.PostalCode,
        DocumentBusinessPartnerEntity.Country,
        DocumentBusinessPartnerEntity.Email,
        DocumentBusinessPartnerEntity.PhoneNumber,
        DocumentBusinessPartnerEntity.FirstName,
        DocumentBusinessPartnerEntity.LastName
    ].reduce((acc: TFieldsDefinition, propName: string) => {
        const path = `${bpPath}${propName}`;
        acc[path] = getBusinessPartnerFieldDef(propName, args);
        return acc;
    }, {});
};

export const isBusinessPartnerField = (name: string): boolean => {
    return name === "BusinessPartner/Name" || name === "BusinessPartner/LegalNumber";
};

export const loadPartner = async (partnerId: string, oData: OData): Promise<IBusinessPartnerEntity> => {
    const response = await oData
        .getEntitySetWrapper(EntitySetName.BusinessPartners)
        .query(partnerId)
        .expand("BankAccounts", (q) => {
            q.select("Id", "AccountNumber", "AbaNumber", "BankCode", "IBAN", "SWIFT", "IsDefaultForReceivedDocuments", "BankAccountRegistrationStatusCode");
            q.orderBy("Order");
            q.expand("Country");
        })
        .expand("Contacts", (q) => {
            q.select("Id", "FirstName", "LastName", "Email", "PhoneNumber").orderBy("Order");
        })
        .expand("ReceivedDocumentDefault", q => {
            q.expand("AccountAssignment");
            q.expand("VatClassification");
        })
        .expand("BillingAddress", q => {
            q.expand("Country");
        })
        .expand("IssuedDocumentDefault", q => {
            q.expand("AccountAssignment");
            q.expand("VatClassification");
            q.expand("CompanyBankAccount", qq => {
                qq.expand("Country");
            });
        })
        .expand("VatStatus")
        .fetchData<IBusinessPartnerEntity>();

    return response?.value ?? {};
};

export const fetchBPAccountsFromAres = async (taxNumber: string) => {
    const UNRELIABLE = [12, 2];
    if (taxNumber) {
        const response = await fetch(`${ARES_GET_PAYER_STATUS_API}?taxNumber=${taxNumber}`);
        if (response.ok) {
            const result = await response.json();

            const _getCountry = (item: TRecordAny) => {
                return item?.IBAN?.startsWith?.("SK") ? CountryCode.Slovakia : CountryCode.CzechRepublic;
            };

            return {
                isAres: true,
                Accounts:
                    result?.BankAccountList?.filter((item: TRecordAny) => BPAccountSchema.isValidSync(item)).map((item: yup.InferType<typeof BPAccountSchema>, index: number) => {
                        let {
                            BankCode,
                            AccountNumber,
                            Iban,
                          } = item;

                          const ibanValid = IBAN.isValid(Iban);

                          if (ibanValid && (!AccountNumber || !BankCode)) {
                            const parsed = createAccountFromIBAN(Iban);
                            if (parsed) {
                              AccountNumber = parsed.account;
                              BankCode = parsed.bank;
                            }
                          }

                          if (!ibanValid && AccountNumber && BankCode && item.CountryCode) {
                            Iban = createIBANFromAccount(AccountNumber, BankCode, item.CountryCode);
                          }

                        return {
                          ...item,
                          AccountNumber,
                          BankCode,

                          Id: index,
                          BankAccountRegistrationStatusCode: BankAccountRegistrationStatusCode.Registered,
                          Country: {
                            Code: _getCountry(item),
                          },
                        };
                      }) || [],
                PayerReliabilityStatusCode: UNRELIABLE.indexOf(result.ActualVatStatus) === -1 ? PayerReliabilityStatusCode.Reliable :
                    PayerReliabilityStatusCode.Unreliable
            };
        }
    }

    return {
        isAres: false,
        PayerReliabilityStatusCode: null,
        Accounts: []
    };
};

export const fetchAresDetail = async (legalNumber: string, countryCode: CountryCode.CzechRepublic | CountryCode.Slovakia): Promise<IBusinessPartnerEntity & {
    MainActivity: string,
    LegalForm: number
}> => {
    const response = await fetch(`${ARES_GET_API}?country=${countryCode}&identificationNumber=${legalNumber}`);

    if (!response.ok) {
        logger.error("couldn't fetch from ARES API");
        return null;
    }

    const item = await response.json();

    const taxNumber = item.Record.VatInfo?.VatIdentificationNumber;
    const detailData = await fetchBPAccountsFromAres(taxNumber);

    const { Street, City, HouseNumber, Country, PostalCode } = item.Record.Address;

    return {
        Id: null, // clears businessPartner relation as this is a new one
        LegalForm: parseInt(item.Record.LegalForm),
        BillingAddress: {
            City, PostalCode,
            CountryCode: Country,
            Country: {
                Code: Country,
                IsEuMember: true, // we support only SK and CZ now...
                IsEEA: true
            },
            Street: (Street || City) ?? "",
            ...splitAddressNumber(HouseNumber)
        },
        TaxNumber: taxNumber,
        PayerReliabilityStatusCode: detailData.PayerReliabilityStatusCode,
        BankAccounts: detailData.Accounts,
        VatStatus: {
            Code: item.Record.VatInfo?.VatPayer ? VatStatusCode.VATRegistered : item.Record.IsIdentifiedPersonForVat ? VatStatusCode.IdentifiedPerson : VatStatusCode.NotVATRegistered
        },
        MainActivity: item.Record.MainNaceActivity
    };
};


export function setBusinessPartnerGroupStatus(storage: FormStorage, isCreating: boolean, bpPath = "BusinessPartner"): void {
    if (storage.isReadOnly) {
        // when storage is in read-only mode, we don't want to show group status as creating, even there is no BP ID
        isCreating = false;
    }
    setGroupStatus(getCollapsedGroupId({ id: `${bpPath}/TaxNumber` }), storage, isCreating);
}

export function isCreatingBP(storage: FormStorage, bpPath = "BusinessPartner"): boolean {
    const status = storage.getGroupStatus(getCollapsedGroupId({ id: `${bpPath}/TaxNumber` }));
    return !!status.isCreating;
}

interface IBusinessPartnerChangeArgs {
    allowCreate?: boolean;
    currencyDatePropPath?: string;
}

type TEntityBusinessPartner =
    IBankTransactionBusinessPartnerEntity
    | IDocumentBusinessPartnerEntity
    | IPrDeductionBusinessPartnerEntity;

const resetContactItems = (storage: FormStorage) => {
    const fields = [BusinessPartnerContactEntity.Email, BusinessPartnerContactEntity.PhoneNumber,
        BusinessPartnerContactEntity.FirstName, BusinessPartnerContactEntity.LastName];
    const bcRoot = storage.data.bindingContext.navigate("BusinessPartner");

    invalidateItems(fields.map(field => bcRoot.navigate(field)), storage);
};

export const handleBusinessPartnerChange = async (args: IBankAccountArgs, e: ISmartFieldChange, opts?: IBusinessPartnerChangeArgs): Promise<void> => {
    const { currencyDatePropPath } = opts ?? {};
    const isBPMainField = isBusinessPartnerField(e.bindingContext.getNavigationPath(true));
    const bpPath = (args.businessPartnerPath ?? "BusinessPartner") as keyof TBankAccountRelatedEntity;
    const bpEntity = args.storage.data.entity[bpPath] as TEntityBusinessPartner;

    const path = e.bindingContext.getPath();
    const parentBc = e.bindingContext.getParent();
    const parentPath = parentBc.getPath(true);
    const isBPChange = parentPath === BankTransactionEntity.BusinessPartner;


    const isNew = args.storage.data.bindingContext.isNew();
    const updateVatStatus = isNew && isBPChange && (path === DocumentBusinessPartnerEntity.TaxNumber || (isBPMainField && e.triggerAdditionalTasks));
    if (updateVatStatus) {
        // default VAT status
        const isTaxNumberSet = isBPMainField ? !!e.additionalData?.TaxNumber : !!e.value;
        const currentVatStatus = bpEntity?.VatStatus?.Code;
        const statusBc = parentBc.navigate(DocumentBusinessPartnerEntity.VatStatus);
        if (currentVatStatus === VatStatusCode.NotVATRegistered && isTaxNumberSet && !e.additionalData?.[DocumentBusinessPartnerEntity.VatStatusCode]) {
            args.storage.setValue(statusBc, VatStatusCode.VATRegistered);
            args.storage.refreshFields(true, true);
        }
    }

    if (isBPMainField) {
        if (e.triggerAdditionalTasks) {
            const { Id } = e.additionalData ?? {};

            appendAdditionalItemsToSavedBankAccounts(args);
            setSavedContactItems(args.storage, e.additionalData.Contacts);
            setSavedBankAccounts(args.storage, e.additionalData.BankAccounts);
            if (!bpEntity.BusinessPartner) {
                bpEntity.BusinessPartner = {};
            }
            bpEntity.BusinessPartner.Contacts = e.additionalData.Contacts;
            bpEntity.BusinessPartner.BankAccounts = e.additionalData.BankAccounts;

            resetContactItems(args.storage);
            args.storage.refresh();

            // todo: better condition?? Also manual change, different behavior blur vs. change
            if (e.additionalData?.isAres) {
                // BP from ARES is loaded async, trigger blur actions immediately after setting BP from ARES
                handleBusinessPartnerBlur(args, {
                    wasChanged: true,
                    bindingContext: e.bindingContext,
                    origEvent: null
                });
            }

        } else if (bpEntity?.BusinessPartner) {
            // User changes only Name/LegalNumber without picking from list -> remove the relation
            bpEntity.BusinessPartner = undefined;
        }
    }
    const isBPCountry = path === DocumentBusinessPartnerEntity.Country && isBPChange;
    if (opts.currencyDatePropPath && (isBPMainField || isBPCountry) && e.triggerAdditionalTasks) {
        // change currency if there are no changed items
        if (!(args.storage.data.entity as IDocumentExtendedEntity).Items?.some((item: IDocumentItemEntity) => item.TransactionAmount)) {
            await setDefaultBPCurrency(args, e, currencyDatePropPath);
        }
    }

    if (e.bindingContext.getPath() === DocumentBusinessPartnerEntity.VatStatus || updateVatStatus) {
        args.storage.refreshGroupByKey("partner");
    }
};


interface ISetDefaultsFromBusinessPartner {
    storage: FormStorage<IDocumentExtendedEntity>;
    type?: DocumentTypeCode;
    businessPartner?: IBusinessPartnerEntity;
    isAres: boolean;
}

export const setDefaultsFromBusinessPartner = async ({
                                                         storage,
                                                         businessPartner,
                                                         type,
                                                         isAres
                                                     }: ISetDefaultsFromBusinessPartner, refreshDateDue?: (dueDays?: number) => void): Promise<void> => {
    let defaultBankAccount;
    const isReceivedDoc = isReceived(type);
    const propName = isReceivedDoc ? BusinessPartnerEntity.ReceivedDocumentDefault : BusinessPartnerEntity.IssuedDocumentDefault;

    // common properties for both ReceivedDocumentDefault and IssuedDocumentDefault
    if (businessPartner[propName] && !isObjectEmpty(businessPartner[propName])) {
        if (businessPartner[propName].DefaultPaymentMethodCode) {
            storage.clearAndSetValueByPath("PaymentMethod", businessPartner[propName].DefaultPaymentMethodCode);
        }

        const props: (keyof (IBusinessPartnerIssuedDocumentDefaultEntity | IBusinessPartnerReceivedDocumentDefaultEntity))[] =
            ["SymbolSpecific", "SymbolVariable", "SymbolConstant", "RemittanceInformation"];

        props.forEach(prop => {
            if (businessPartner[propName][prop]) {
                storage.setValueByPath(prop, businessPartner[propName][prop]);
            }
        });

        if (isDefined(businessPartner[propName].DaysDue)) {
            // DaysDue depends on DateAccountingTransaction -> call common method if present
            refreshDateDue?.(businessPartner[propName].DaysDue);
        }

        if (isAccountAssignmentCompany(storage.context)) {
            const accountAssignment = businessPartner[propName]?.AccountAssignment;

            if (!isObjectEmpty(businessPartner[propName].AccountAssignment)) {
                storage.setValueByPath(DocumentEntity.AccountAssignmentSelection, convertAccountAssignmentIntoSelection(accountAssignment));
                storage.setValueByPath(`${DocumentEntity.AccountAssignmentSelection}/${AccountAssignmentSelectionEntity.Selection}`, SelectionCode.Copy);

                const fiscalYear = storage.data.entity.FiscalYear;
                const items = fiscalYear ? (await loadAccountAssignments(storage)) : [];

                await rewriteAccountAssignment({
                    storage,
                    choaId: fiscalYear?.ChartOfAccounts?.Id,
                    itemBc: storage.data.bindingContext,
                    accountAssignments: items
                });
            }
        }
    }


    // some other things?
    if (!isAres) {
        if (isReceivedDoc) {
            defaultBankAccount = businessPartner.BankAccounts?.find(
                (account: IBusinessPartnerBankAccountEntity) => account.IsDefaultForReceivedDocuments
            );
        } else if (businessPartner.IssuedDocumentDefault?.CompanyBankAccount?.IsActive) {
            defaultBankAccount = businessPartner.IssuedDocumentDefault?.CompanyBankAccount;
        } else if (businessPartner.IssuedDocumentDefault?.DefaultPaymentMethodCode === PaymentMethodCode.PaymentService) {
            const defAcc = storage.context.getCompanyBankAccounts()?.filter((acc: ICompanyBankAccountEntity) => acc.IsDefault)?.[0];
            if (defAcc && defAcc.PaymentServiceID) {
                // as we can't store default acc for payment service in business partner we use default one if it is the case
                defaultBankAccount = defAcc;
            }
        }

        // If no default bank account is set, use the first one https://solitea-cz.atlassian.net/browse/DEV-34376
        if (!defaultBankAccount && businessPartner.BankAccounts?.length && isReceivedDoc) {
            defaultBankAccount = businessPartner.BankAccounts.find(account => !account.PaymentServiceID);
        }

        if (defaultBankAccount && !defaultBankAccount?.PaymentServiceID) {
            storage.processDependentField(BankAccountDependentFields, defaultBankAccount);
        }
    }

    prepareSavedAccounts({
        storage: storage,
        type: type,
        businessPartner,
        account: defaultBankAccount
    });

    storage.refresh();
};

const setDefaultBPCurrency = async (args: IBankAccountArgs, e: ISmartFieldChange, datePropPath?: string): Promise<void> => {
    const currencyBc = args.storage.data.bindingContext.navigate("TransactionCurrency");
    // country or BP change
    const countryCode = e.bindingContext.getPath() === "Country" ? e.additionalData.Code : e.additionalData.Country?.Code;
    if (countryCode) {
        let currencyCode: CurrencyCode;
        if (countryCode === getAgendaCountryCode(args.storage.context)) {
            // Czech republic is default, we don't have it "currenciesUsedByCompany" and always switch it back
            currencyCode = getCompanyCurrency(args.storage.context);
        } else {
            const res = await args.storage.oData.getEntitySetWrapper(EntitySetName.Countries).query(`'${countryCode}'`).select("DefaultCurrencyCode").fetchData<ICountryEntity>();
            const defaultCurrency = res.value?.DefaultCurrencyCode as CurrencyCode;
            const usedCurrencies = getCompanyUsedCurrencyCodes(args.storage.context);
            const isUsed = usedCurrencies.includes(defaultCurrency);
            if (isUsed) {
                currencyCode = defaultCurrency;
            }
        }
        if (currencyCode) {
            args.storage.setValue(currencyBc, currencyCode);
            await refreshExchangeRate(args.storage, currencyCode, datePropPath);
            // invalidate item templates cache
            invalidateItemTemplateSelectItems(args.storage);
            args.storage.addActiveGroupByKey("Items");
            args.storage.refresh();
        }
    }
};

const GET_EXISTING_BP_URL = `${REST_API_URL}/BusinessPartner/Existing`;

/**
 * Function returns
 *  ... BP data if already exists,
 *  ... true if it will be created based on the passed data and
 *  ... false if the data are invalid or insufficient
 * @param storage
 * @param businessPartner
 */
export async function getExistingBusinessPartner(storage: FormStorage, businessPartner: IDocumentBusinessPartnerEntity, bpBindingContext: BindingContext): Promise<IBusinessPartnerEntity | boolean> {
    try {
        const streetPath = createPath(DocumentEntity.BusinessPartner, DocumentBusinessPartnerEntity.Street);
        if (businessPartner?.BusinessPartner?.Id) {
            // existing BP is linked, no need to check anything, just return and keep the current relation
            return businessPartner?.BusinessPartner;
        }
        const postData = cloneDeep(businessPartner);
        delete postData.BusinessPartner;
        postData.CountryCode = businessPartner.CountryCode ?? businessPartner.Country?.Code;
        delete postData.Country;

        const res = await customFetch(GET_EXISTING_BP_URL, {
            ...getDefaultPostParams(),
            body: JSON.stringify(postData)
        });

        if (res.ok) {
            const data = parseResponse<IBusinessPartnerEntity>(res);
            if (isODataError(data)) {
                logger.error("Error fetching existing business partner: ", data as ODataError);
                return false;
            }
            // BE returns the whole domain entity
            // clean it to match the FE OData definition before it gets set to the storage, otherwise it would cause error
            return BindingContext.clean(data, bpBindingContext) as IBusinessPartnerEntity;
        }
        // we always create new BP when its address is mandatory (even if not filled yet)
        // ... or BE responds with 404 (not found)
        return res.status === HTTPStatusCode.NotFound || isRequiredByPath(storage, streetPath);
    } catch (error) {
        // fall silently as we just won't be able to link existing BP, but that's something BE anyway handles later
        // only issue I can see now is that business partner defaults might not be applied correctly without this link
        logger.error("Error fetching existing business partner: ", error);
    }
    return false;
}

export const showBpReliableStatus = (context: IAppContext): boolean => {
    return hasEverBeenVatRegisteredCompany(context);
};

export function showAccountReliableStatus(context: IAppContext, type?: DocumentTypeCode): boolean {
    /*isVatRegisteredCompany(context) &&*/
    // show only according to document type - we show the account reliability status also for non-VAT registered Agendas,
    // because it prevents payments to unreliable accounts (e.g. fake invoices, etc...)
    return isNotDefined(type) || isSupportingAccountReliability(type);
}

export const loadBpAndSetCorrectGroup = async (storage: FormStorage, bc: BindingContext) => {
    const bp = storage.getValue(bc.getParent());
    const bpNavigation = bc.getParent().navigate(DocumentBusinessPartnerEntity.BusinessPartner);

    const existingBP = await getExistingBusinessPartner(storage, bp, bpNavigation);
    if (typeof existingBP === "object" && existingBP?.Id) {
        storage.setValue(bpNavigation, existingBP);
    } else {
        storage.clearValue(bpNavigation);
    }
    const isNew = existingBP === true;
    setBusinessPartnerGroupStatus(storage, isNew);

    return isNew;
};

export const handleBusinessPartnerBlur = async (args: IBankAccountArgs, e: ISmartFieldBlur): Promise<void> => {
    const isEmployeeChange = e.bindingContext.getPath(true) === DocumentBusinessPartnerEntity.Employee;
    const isBusinessPartnerField = e.bindingContext.getParent().getPath(true) === (args.businessPartnerPath ?? "BusinessPartner");

    if (isBusinessPartnerField && e.wasChanged) {
        const { storage } = args;

        const bp = storage.getValue(e.bindingContext.getParent());

        // no id is set, but there are some data in inputs => we expect creating new item
        const isNew = isEmployeeChange ? false : await loadBpAndSetCorrectGroup(storage, e.bindingContext);

        if (isNew) {
            // clear accounts and BP related data -> businessPartner relation was removed
            const aresAccounts = (args.storage as FormStorage<unknown, IDocumentCustomData>).getCustomData().aresBankAccounts;
            if (!aresAccounts) {
                prepareSavedAccounts({ ...args, businessPartner: {} });
            }
            setSavedContactItems(args.storage);
        }
        if (isEmployeeChange) {
            prepareSavedAccounts(args);
        }
        updateAllSavedAccountsOnPartnerChange({ ...args, businessPartner: bp as IBusinessPartnerEntity });

        args.storage.refreshGroup(e.bindingContext);
        return;
    }
};

export function updateBPRelation(data: IEntity, path = "BusinessPartner"): void {
    if (data[path]?.BusinessPartner?.Id) {
        // Existing Businesspartner, keep only Id in the request
        const bpId = data[path].BusinessPartner.Id;
        data[path].BusinessPartner = { Id: bpId };
    } else if (data[path]) {
        // delete relation and its remains if not set
        data[path].BusinessPartner = null;
    }
}

export function clearBPIfNotSet(data: IEntity, path = "BusinessPartner"): boolean {
    if (data[path] && !data[path].BusinessPartner?.Id) {
        // If there is no relation to BP entity, we remove it completely. BE creates the BP on its own
        delete data[path].BusinessPartner;
    }
    if (!data[path] || (!data[path].Name && !data[path].LegalNumber && !data[path].Employee?.Id)) {
        // not a new BusinessPartner
        // BusinessPartner can be optional, but there can be some default values in its object, like Currency
        // => remove it completely, so that it isn't send to BE at all
        data[path] = null;
        return true;
    }
    return false;
}

const compareContact = (c1: IBusinessPartnerContactEntity, c2: IBusinessPartnerContactEntity) => {
    return areObjectsEqual<IBusinessPartnerContactEntity>(c1, c2,
        ["FirstName", "LastName", "PhoneNumber", "Email"]);
};

export const setSavedContactItems = (storage: FormStorage<unknown, IBusinessPartnerCustomData>, contacts: IBusinessPartnerContactEntity[] = []): void => {
    storage.setCustomData({ businessPartnerContacts: contacts });
};

export const setSavedBankAccounts = (storage: FormStorage<unknown, IBankAccountsCustomData>, bankAccounts: IBankAccountEntity[] = []): void => {
    storage.setCustomData({ bankAccounts: bankAccounts });
};


export const hasBusinessPartner = (storage: FormStorage, partner?: IBusinessPartnerEntity, path = "BusinessPartner"): boolean => {
    partner = !isObjectEmpty(partner) ? partner : storage.data.entity[path];
    return !!(partner?.Id || partner?.Name || partner?.LegalNumber);
};


export const hasMatchingBusinessPartnerContact = (storage: FormStorage<any, IBusinessPartnerCustomData>, currentContact: IBusinessPartnerContactEntity, path: string): boolean => {
    const findSameContact = (contacts: IBusinessPartnerContactEntity[]) => {
        return contacts.find((contact: IBusinessPartnerContactEntity) => compareContact(currentContact, contact));
    };

    return !!findSameContact(storage.getCustomData().businessPartnerContacts) || !!findSameContact(storage.data.entity[path]?.BusinessPartner?.Contacts ?? []);
};

const isEmptyContact = (contact: IBusinessPartnerContactEntity): boolean => {
    return contact && !contact.FirstName && !contact.LastName && !contact.Email && !contact.PhoneNumber;
};

export const addBusinessPartnerContactRequest = (storage: FormStorage, isNewBusinessPartner: boolean, batch: BatchRequest, path = "BusinessPartner"): void => {
    const extractedBusinessPartner = storage.extractFields<IBusinessPartnerEntity>(businessPartnerDependentFields());
    const newContact = extractedBusinessPartner.Contacts[0];

    if (isEmptyContact(newContact) || isNewBusinessPartner || hasMatchingBusinessPartnerContact(storage, newContact, path)) {
        return null;
    }

    delete newContact.Id;
    // Put to the end
    newContact.Order = 0;

    batch.fromPath(`BusinessPartners(${storage.data.entity[path].BusinessPartner.Id})/Contacts`).create(newContact);

    return null;
};

export function isBusinessPartner(fullName: string): boolean {
    return fullName === "Solitea.Evala.DomainModel.DocumentBusinessPartner"
        || fullName === "Solitea.Evala.DomainModel.BankTransactionBusinessPartner"
        || fullName === "Solitea.Evala.DomainModel.GrantBusinessPartner";
}